import { FC, useCallback, useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import Image from './Image';
import BlockTaskQuiz, { TaskResponse } from './BlockTask_Quiz';
import BlockTaskWordGame from './BlockTask_WordGame';
import { LightboxItem, LightboxWrapper } from './LightboxModal';
import { AppContext } from '../contexts/AppContext';
import { getTranslation } from '../common/Translation';
import { TaskBlock, TaskCard, TaskCardImage, TaskCardContent, TaskEyebrow, TaskQuestion, TaskFeedback, TaskNavigation, TaskButton, TaskTextButton } from './TaskCard';

interface BlockTaskProps {
  taskList?: any
}

const NavTextButton = styled(TaskTextButton)`
  margin-top: 0;
`;

/**
 * Render a task block. Answers are shown in place (green/red), with the feedback as a band under the card.
 * Several tasks are stepped through with previous/next and end with a summary. Everything resets in idle mode.
 * @returns {JSX.Element} Component template
 */
const BlockTask: FC<BlockTaskProps> = ({taskList}) => {
  const { t } = useTranslation();
  const { currentLanguage, idleResetCount } = useContext(AppContext);
  const [currentTaskNumber, setCurrentTaskNumber] = useState<number>(0);
  const [responses, setResponses] = useState<TaskResponse[]>([]);
  const [showSummary, setShowSummary] = useState<boolean>(false);
  const [currentMedia, setCurrentMedia] = useState<any>(null);

  const taskCount = taskList?.length || 0;
  const currentTask = taskList?.[currentTaskNumber];
  const response = responses[currentTaskNumber];
  const isLastTask = currentTaskNumber >= taskCount - 1;
  const score = responses.filter(r => r?.isCorrect).length;

  /**
   * Store the answer for the current task. A task can only be answered once.
   */
  const taskIsFinished = useCallback((isCorrect: boolean, answer?: number, responseText?: string) => {
    setResponses(prev => {
      if (prev[currentTaskNumber]) { return prev; }
      const next = [...prev];
      next[currentTaskNumber] = { isCorrect, answer, responseText };
      return next;
    });
  }, [currentTaskNumber]);

  const showNextTask = useCallback(() => {
    if (isLastTask) {
      setShowSummary(true);
    } else {
      setCurrentTaskNumber(n => n + 1);
    }
  }, [isLastTask]);

  // Start over when idle mode resets the kiosk, so the next visitor gets fresh tasks
  useEffect(() => {
    setResponses([]);
    setCurrentTaskNumber(0);
    setShowSummary(false);
  }, [idleResetCount]);

  if (!taskCount) { return null; }

  if (showSummary) {
    return (
      <TaskBlock>
        <TaskCard className="showInPreview">
          <TaskCardContent>
            <TaskEyebrow>{t("task.summary")}</TaskEyebrow>
            <TaskQuestion>{t("task.youAnsweredCorrectOnXTasks").replace("{correctAnswers}", String(score)).replace("{totalTasks}", String(taskCount))}</TaskQuestion>
            <TaskNavigation>
              <NavTextButton onClick={() => setShowSummary(false)}>{t("task.prev")}</NavTextButton>
            </TaskNavigation>
          </TaskCardContent>
        </TaskCard>
      </TaskBlock>
    );
  }

  const feedbackText = response && (response.responseText || getTranslation(currentTask?.resultFeedback, currentLanguage));

  return (
    <TaskBlock>
      <TaskCard className={`showInPreview${currentTask?.cover?.mediaId ? " withImage" : ""}`}>
        {currentTask?.cover?.mediaId && (
          <LightboxWrapper>
            <LightboxItem media={currentMedia}>
              <TaskCardImage>
                <Image key={currentTaskNumber} media={currentTask.cover} setMedia={setCurrentMedia}/>
              </TaskCardImage>
            </LightboxItem>
          </LightboxWrapper>
        )}

        <TaskCardContent>
          {taskCount > 1 && (<TaskEyebrow>{`${t("task.task")} ${currentTaskNumber + 1} ${t("task.of")} ${taskCount}`}</TaskEyebrow>)}
          {currentTask?.taskType === "quiz" && (
            <BlockTaskQuiz taskData={currentTask} userAnswer={response} completeCallback={taskIsFinished}/>
          )}
          {currentTask?.taskType === "wordgame" && (
            <BlockTaskWordGame key={currentTaskNumber} taskData={currentTask} completeCallback={taskIsFinished}/>
          )}

          {taskCount > 1 && (
            <TaskNavigation>
              <NavTextButton onClick={() => setCurrentTaskNumber(n => n - 1)} disabled={currentTaskNumber === 0}>{t("task.prev")}</NavTextButton>
              <TaskButton onClick={showNextTask} disabled={!response}>{isLastTask ? t("task.finish") : t("task.next")}</TaskButton>
            </TaskNavigation>
          )}
        </TaskCardContent>

        {response && (
          <TaskFeedback className={response.isCorrect ? "correct" : "wrong"} role="status">
            <strong>{response.isCorrect ? t("task.correct") : t("task.wrong")}</strong>
            {feedbackText}
          </TaskFeedback>
        )}
      </TaskCard>
    </TaskBlock>
  );
}

export default BlockTask;
